import { formatEventDate } from "../utils/formatEventDate"
import MasonryCollection from "./components/MasonryCollection"
import { GalleryDesignProps } from "./types"
import PhotographerBar from "./components/PhotographerBar"
import CoverImage from "./components/CoverImage"

/**
 * 01 · Editorial & Luxe
 * Light + dark. Classic serif hero with a full-bleed cover, then a 3-column
 * (2 on mobile) masonry collection. Color mode comes from the body `dark`
 * class set by useTheme, so the same component serves both variants.
 */
const EditorialLuxeGallery = ({
  data,
  folderBar,
  contentMode,
  aiContent,
  emptyContent,
  photos,
  onCoverLoad,
}: GalleryDesignProps) => {
  const name = data?.name || ""
  const dateLabel = formatEventDate(data?.startDate, data?.endDate, data?.date)
  const cover = data?.coverPhotoLink
  const vendor = data?.vendors?.[0]

  return (
    <div className="relative w-full bg-white text-[#141414] dark:bg-[#0c0c0c] dark:text-[#efece6] font-hanken">
      {/* Photographer bar — logo + name top-left, icons top-right of screen */}
      <PhotographerBar vendor={vendor} tone="light" />

      {/* Cover */}
      <div className="w-full h-[60vh] md:h-[86vh] bg-[#ececec] dark:bg-[#1b1b1b] overflow-hidden">
        {cover && (
          <CoverImage
            src={cover}
            alt={name}
            className="w-full h-full object-cover"
            onLoad={onCoverLoad}
          />
        )}
      </div>

      {/* Title block */}
      <div className="text-center px-6 pt-6 pb-8">
        <h1 className="font-cormorant font-light italic leading-[0.95] tracking-[0.01em] text-[36px] md:text-[54px] lg:text-[54px] break-words">
          {name}
        </h1>
        {dateLabel && (
          <div className="mt-4 mb-4 flex items-center justify-center gap-3 md:gap-4">
            <span className="h-px w-8 md:w-12 bg-[#dcdcdc] dark:bg-white/30" />
            <span className="text-[11px] md:text-[12px] font-medium tracking-[0.36em] uppercase text-[#8a8a8a] dark:text-[#9a9a9a]">
              {dateLabel}
            </span>
            <span className="h-px w-8 md:w-12 bg-[#dcdcdc] dark:bg-white/30" />
          </div>
        )}
      </div>

      {/* Folder bar + collection share one tall parent so the sticky folder
          bar stays pinned (below the header) while scrolling the photos. */}
      <div className="flex flex-col items-center w-full">
        {folderBar}

        <div className="w-full px-4 md:px-14 pt-6 pb-10 min-h-[40vh]">
          {contentMode === "ai" ? (
            aiContent
          ) : contentMode === "photos" ? (
            <MasonryCollection
              photos={photos}
              columnsClass="columns-2 md:columns-3"
            />
          ) : (
            emptyContent
          )}
        </div>
      </div>
    </div>
  )
}

export default EditorialLuxeGallery
